'use client';

import * as React from 'react';
import Link from 'next/link';
import { Car, Fuel, GaugeCircle } from 'lucide-react';

import { routes } from '@workspace/routes';
import { Badge } from '@workspace/ui/components/badge';
import { buttonVariants } from '@workspace/ui/components/button';
import { cn } from '@workspace/ui/lib/utils';

import {
  carCategoryLabel,
  fuelTypeLabel,
  transmissionTypeLabel
} from '~/lib/labels';
import type { CarDto } from '~/types/dtos/car-dto';

export function CarCard({ car }: { car: CarDto }): React.JSX.Element {
  return (
    <div className="border-border bg-background group overflow-hidden rounded-lg border transition-all duration-200 hover:shadow-sm">
      {/* Image container */}
      <Link href={`${routes.marketing.Cars}/${car.slug}`}>
        <div className="relative aspect-[4/3] cursor-pointer overflow-hidden bg-black">
          <img
            src={car.mainImage}
            alt={car.name}
            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
            className="size-full max-h-full max-w-full object-cover transition-transform duration-300 group-hover:scale-105"
          />

          {/* Featured badge - top left */}
          {car.featured && (
            <div className="absolute left-2 top-2">
              <Badge variant="default">Premium</Badge>
            </div>
          )}

          {/* Status badges - top right */}
          <div className="absolute right-2 top-2 flex flex-row gap-2">
            {car.isNew && <Badge variant="secondary">Nou</Badge>}
            {car.onSale && <Badge variant="destructive">Discount</Badge>}
          </div>
        </div>
      </Link>

      {/* Category badge */}
      <div className="px-4 pt-4">
        <Badge variant="outline">{carCategoryLabel[car.category]}</Badge>
      </div>

      {/* Car title */}
      <Link href={`${routes.marketing.Cars}/${car.slug}`}>
        <div className="px-4 pt-1">
          <h3 className="text-foreground hover:text-primary text-lg font-bold transition-colors">
            {car.name}
          </h3>
        </div>
      </Link>

      {/* Car specs */}
      <div className="flex flex-wrap gap-4 px-4 pt-2">
        <div className="text-muted-foreground flex items-center text-xs">
          <Fuel className="mr-1 h-4 w-4" />
          {fuelTypeLabel[car.fuelType]}
        </div>
        <div className="text-muted-foreground flex items-center text-xs">
          <GaugeCircle className="mr-1 h-4 w-4" />
          {car.power ? `${car.power} CP` : 'N/A'}
        </div>
        <div className="text-muted-foreground flex items-center text-xs">
          <Car className="mr-1 h-4 w-4" />
          {transmissionTypeLabel[car.transmissionType]}
        </div>
      </div>

      {/* Pricing and action button */}
      <div className="border-border bg-muted/10 mt-4 flex items-center justify-between border-t px-4 py-3">
        <div>
          {car.onSale && car.salePrice ? (
            <div className="flex items-center gap-2">
              <span className="text-foreground text-xl font-bold">
                {car.salePrice}€
              </span>
              <span className="text-muted-foreground text-xs">/zi</span>
              <span className="text-muted-foreground text-xs line-through">
                {car.price}€
              </span>
            </div>
          ) : (
            <div className="flex items-center gap-1">
              <span className="text-foreground text-xl font-bold">
                {car.price}€
              </span>
              <span className="text-muted-foreground text-xs">/zi</span>
            </div>
          )}
        </div>
        <Link
          href={`${routes.marketing.Cars}/${car.slug}`}
          className={cn(
            buttonVariants({
              variant: 'default',
              size: 'sm'
            }),
            'h-8 rounded-md px-3 py-0 text-xs font-medium'
          )}
        >
          Detalii
        </Link>
      </div>
    </div>
  );
}
